/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/assessments/getRecipResponse.js
 *
 * RECIP 1.0 E-3 — voxel-level PSMA-VOL response computation.
 *
 * E-3 changes from error-fix version:
 *
 *   1. computePsmaVolAtTimepoint no longer classifies lesions — it just sums
 *      roi.psmaPosVolumeByTimepoint[ctSeriesUid] across tumour ROIs.
 *      All classification happened in computePsmaPosVolumesFromBridge (E-2).
 *
 *   2. NE propagation: if any tumour ROI's psmaPosVolume is null (NE) at a tp,
 *      the entire tp's PSMA-VOL is null → NE downstream. The first null short-
 *      circuits the sum (no need to accumulate).
 *
 *   3. 'calculating' state: if any ROI is still calculating, PSMA-VOL is
 *      treated as 'calculating' → assessments should not be finalized yet.
 *      Represented as a sentinel (NE) for now; the UI signals loading state
 *      separately via useSuvComputing().
 *
 *   4. getEffectivePsma import removed entirely (no per-ROI PSMA status).
 *      threshold self-healing logic removed (not needed — volumes come pre-
 *      computed on ROIs).
 *
 *   5. getRecipAssessments signature unchanged (same 5 params) but thresholds
 *      is now unused internally — kept for API compat with calling sites that
 *      pass it from computePsmaPosVolumesFromBridge.
 *
 * New-lesion detection, response thresholds, PD monotonic, nadir/previous
 * skip-NE logic — all unchanged from D.
 */

import { TRACKING_TYPES } from './guideline';

const RECIP_DEBUG =
  typeof window !== 'undefined' && window.__RECIP_DEBUG === true;

const log = (...args) => {
  if (RECIP_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('[RECIP]', ...args);
  }
};

/**
 * Tumour-cohort trackingTypes (v5 + legacy v3/v4 compat).
 * Blood-pool and liver-reference are NEVER in this set.
 */
const TUMOUR_TYPES = new Set([
  TRACKING_TYPES.LESION,
  TRACKING_TYPES.NEW_LESION,
  // legacy v3:
  TRACKING_TYPES.TARGET,
  TRACKING_TYPES.PSMA_NEGATIVE,
  TRACKING_TYPES.NEW_PSMA_POSITIVE,
  TRACKING_TYPES.NEW_PSMA_NEGATIVE,
  // legacy generic:
  TRACKING_TYPES.NEW,
]);

/**
 * New-lesion link types — link-type-only detection, no PSMA gating.
 */
const NEW_LESION_TYPES = new Set([
  TRACKING_TYPES.NEW_LESION,
  TRACKING_TYPES.NEW_PSMA_POSITIVE,
  TRACKING_TYPES.NEW_PSMA_NEGATIVE,
  TRACKING_TYPES.NEW,
]);

function lesionVolume(lesion) {
  if (!lesion) return 0;
  return lesion.Volume || lesion.volume || lesion.totalVolume || 0;
}

/**
 * Compute PSMA-VOL at one timepoint by summing pre-computed psmaPosVolumes.
 *
 * psmaPosVolumeByTimepoint state semantics (from E-2):
 *   number    → valid mm³ PSMA+ volume (can be 0)
 *   null      → NE (no liver reference for soft-tissue, or compute failed)
 *   'calculating' → still computing (bridge not populated)
 *
 * NE propagation: any null → total = null.
 * Calculating: any 'calculating' → total = null (NE until finalized).
 *   The UI handles the loading state separately via useSuvComputing().
 *
 * @param {object} timepoint
 * @param {string} ctSeriesUid
 * @param {Array}  rois          classified ROIContours with psmaPosVolumeByTimepoint
 * @returns {{ totalVolume: number|null, count: number, hasBloodPool: boolean }}
 *   totalVolume === null  ⇒  NE
 *   hasBloodPool: true    ⇒  tp is evaluable (kept for API compat)
 */
function computePsmaVolAtTimepoint(timepoint, ctSeriesUid, rois) {
  const lesions = timepoint?.Lesions || [];
  let total = 0;
  let count = 0;

  for (const lesion of lesions) {
    const roi = rois.find(r => r.ROINumber === lesion.ROINumber);
    if (!roi || !TUMOUR_TYPES.has(roi.trackingType)) continue;
    // Only consider ROIs that have a measurement at this tp
    if (lesionVolume(lesion) <= 0) continue;

    const v = roi.psmaPosVolumeByTimepoint?.[ctSeriesUid];

    // null → NE; 'calculating' → treat as NE (computing)
    if (v === null || v === undefined || v === 'calculating') {
      return { totalVolume: null, count: 0, hasBloodPool: false };
    }

    total += v;
    count++;
  }

  return { totalVolume: total, count, hasBloodPool: true };
}

function getNewLesionsAtTimepoint(timepoint, rois) {
  const names = [];
  const lesions = timepoint?.Lesions || [];
  for (const lesion of lesions) {
    const v = lesionVolume(lesion);
    if (v <= 0) continue;
    const roi = rois.find(r => r.ROINumber === lesion.ROINumber);
    if (!roi) continue;
    if (NEW_LESION_TYPES.has(roi.trackingType)) {
      names.push(roi.ROIName || `ROI ${roi.ROINumber}`);
    }
  }
  return names;
}

export function detectNewLesionPresence(
  newAtTp,
  newNamesAtTp,
  currentIndex,
  accounting = 'CUMULATIVE'
) {
  if (currentIndex === 0) return { present: false, names: [] };
  if (accounting === 'PER_SCAN') {
    const present = !!newAtTp[currentIndex] && !newAtTp[currentIndex - 1];
    return { present, names: present ? newNamesAtTp[currentIndex] : [] };
  }
  const nameSet = new Set();
  for (let i = 1; i <= currentIndex; i++) {
    if (newAtTp[i]) for (const n of newNamesAtTp[i]) nameSet.add(n);
  }
  return { present: nameSet.size > 0, names: [...nameSet] };
}

export function getRecipVolumeResponse(volBaseline, volCurrent, criteria) {
  if (volBaseline === null) {
    return {
      result: 'NE',
      message:
        'Baseline PSMA-VOL not evaluable (no liver-reference ROI or missing SUV at baseline)',
      pct: null,
    };
  }
  if (volCurrent === null) {
    return {
      result: 'NE',
      message:
        'PSMA-VOL not evaluable (no liver-reference ROI or missing SUV at this timepoint)',
      pct: null,
    };
  }
  if (volCurrent === 0) {
    return {
      result: 'CR',
      message: `No PSMA-positive uptake [baseline ${volBaseline.toFixed(
        1
      )} mm³ → 0 mm³]`,
      pct: -1,
    };
  }
  if (volBaseline === 0) {
    return {
      result: 'PD',
      message: `New PSMA-positive disease vs zero baseline [→ ${volCurrent.toFixed(
        1
      )} mm³]`,
      pct: Infinity,
    };
  }
  const pct = (volCurrent - volBaseline) / volBaseline;
  if (pct <= -criteria.minVolumeDecrease) {
    return {
      result: 'PR',
      message: `${(pct * 100).toFixed(
        1
      )}% PSMA-VOL vs baseline [${volBaseline.toFixed(
        1
      )} → ${volCurrent.toFixed(1)} mm³]`,
      pct,
    };
  }
  if (pct >= criteria.minVolumeIncrease) {
    return {
      result: 'PD',
      message: `+${(pct * 100).toFixed(
        1
      )}% PSMA-VOL vs baseline [${volBaseline.toFixed(
        1
      )} → ${volCurrent.toFixed(1)} mm³]`,
      pct,
    };
  }
  return {
    result: 'SD',
    message: `${(pct * 100).toFixed(
      1
    )}% PSMA-VOL vs baseline [${volBaseline.toFixed(1)} → ${volCurrent.toFixed(
      1
    )} mm³]`,
    pct,
  };
}

export function getRecipNewLesionResponse(presence) {
  if (presence.present) {
    const list = presence.names.length ? `: ${presence.names.join(', ')}` : '';
    return { result: 'Unequivocal', message: `New lesion present${list}` };
  }
  return { result: 'Absent', message: 'No new lesion' };
}

export function getRecipOverallResponse(
  allStats,
  newAtTp,
  newNamesAtTp,
  priorOverallResults,
  criteria,
  currentIndex
) {
  if (currentIndex === 0) return { result: '', message: 'Baseline' };

  for (let i = 1; i < currentIndex; i++) {
    if (priorOverallResults[i] === 'PD') {
      return {
        result: 'PD',
        message: `PD monotonic (first declared at follow-up ${i})`,
      };
    }
  }

  const volBaseline = allStats[0]?.totalVolume ?? null;
  const volCurrent = allStats[currentIndex]?.totalVolume ?? null;

  if (volBaseline === null) {
    return {
      result: 'NE',
      message:
        'Baseline PSMA-VOL not evaluable (no liver-reference ROI or missing SUV at baseline)',
    };
  }
  if (volCurrent === null) {
    return {
      result: 'NE',
      message:
        'PSMA-VOL not evaluable (no liver-reference ROI or missing SUV at this timepoint)',
    };
  }
  if (volCurrent === 0) {
    return {
      result: 'CR',
      message:
        `RECIP-CR: PSMA-VOL = 0 (no PSMA-positive uptake anywhere). ` +
        `[PSMA-VOL: baseline ${volBaseline.toFixed(1)} mm³ → current 0 mm³]`,
    };
  }

  const pctVsBaseline =
    volBaseline > 0 ? (volCurrent - volBaseline) / volBaseline : Infinity;

  // Nadir: min PSMA-VOL among evaluable tps before currentIndex (skip NE)
  let volNadir = volBaseline,
    nadirSeen = false;
  for (let i = 0; i < currentIndex; i++) {
    const v = allStats[i]?.totalVolume;
    if (v == null) continue;
    if (!nadirSeen || v < volNadir) {
      volNadir = v;
      nadirSeen = true;
    }
  }
  const pctVsNadir =
    volNadir > 0 ? (volCurrent - volNadir) / volNadir : Infinity;

  // Previous: most recent evaluable tp before currentIndex
  let volPrev = null;
  for (let i = currentIndex - 1; i >= 0; i--) {
    const v = allStats[i]?.totalVolume;
    if (v != null) {
      volPrev = v;
      break;
    }
  }
  const pctVsPrev =
    volPrev != null && volPrev > 0
      ? (volCurrent - volPrev) / volPrev
      : Infinity;

  const hasNewLesion = detectNewLesionPresence(
    newAtTp,
    newNamesAtTp,
    currentIndex,
    criteria.newLesionAccounting
  );

  if (pctVsBaseline <= -criteria.minVolumeDecrease && !hasNewLesion.present) {
    return {
      result: 'PR',
      message:
        `RECIP-PR: PSMA-VOL ${(pctVsBaseline * 100).toFixed(
          1
        )}% vs baseline; no new lesion. ` +
        `[PSMA-VOL: baseline ${volBaseline.toFixed(
          1
        )} mm³ → current ${volCurrent.toFixed(1)} mm³]`,
    };
  }

  const pdRef = (criteria.pdReference || 'NADIR').toUpperCase();
  const pdMap = {
    BASELINE: pctVsBaseline,
    NADIR: pctVsNadir,
    PREVIOUS: pctVsPrev,
  };
  const pctForPd = pdMap[pdRef] ?? pctVsNadir;
  if (pctForPd >= criteria.minVolumeIncrease && hasNewLesion.present) {
    const newNames = hasNewLesion.names.length
      ? `: ${hasNewLesion.names.join(', ')}`
      : '';
    return {
      result: 'PD',
      message:
        `RECIP-PD: PSMA-VOL +${(pctForPd * 100).toFixed(
          1
        )}% vs ${pdRef.toLowerCase()}; ` +
        `new lesion${newNames}. ` +
        `[PSMA-VOL: baseline ${volBaseline.toFixed(
          1
        )} mm³ → current ${volCurrent.toFixed(1)} mm³]`,
    };
  }

  return {
    result: 'SD',
    message:
      `RECIP-SD: vs baseline ${(pctVsBaseline * 100).toFixed(1)}%, ` +
      `vs nadir ${(pctVsNadir * 100).toFixed(1)}%, ` +
      `vs previous ${
        pctVsPrev === Infinity ? 'n/a' : (pctVsPrev * 100).toFixed(1) + '%'
      }, ` +
      `new lesion: ${hasNewLesion ? 'yes' : 'no'}. ` +
      `[PSMA-VOL: baseline ${volBaseline.toFixed(
        1
      )} mm³ → current ${volCurrent.toFixed(1)} mm³]`,
  };
}

/**
 * Top-level RECIP assessment driver.
 *
 * @param {Array}  timepoints
 * @param {Array}  rois          ROIContours with psmaPosVolumeByTimepoint populated (E-2)
 * @param {object} criteria
 * @param {Array}  assessments   reader response overrides
 * @param {object} [thresholds]  Unused in E-3; kept for API compat with callers that pass it.
 */
export default function getRecipAssessments(
  timepoints,
  rois,
  criteria,
  assessments,
  thresholds = {} // eslint-disable-line no-unused-vars
) {
  log('getRecipAssessments input', {
    timepointCount: timepoints.length,
    roiCount: rois.length,
    trackingTypesPresent: [...new Set(rois.map(r => r.trackingType))],
    hasPsmaPosVol: rois.some(r => r.psmaPosVolumeByTimepoint != null),
  });

  const allStats = timepoints.map(tp =>
    computePsmaVolAtTimepoint(tp, tp.SeriesInstanceUID, rois)
  );
  const newNamesAtTp = timepoints.map(tp => getNewLesionsAtTimepoint(tp, rois));
  const newAtTp = newNamesAtTp.map(names => names.length > 0);

  log('per-tp stats', {
    psmaVol: allStats.map(s => s.totalVolume),
    newAtTp,
  });

  const ai_assessments = [];
  const priorOverallResults = [];

  for (let i = 0; i < timepoints.length; i++) {
    const tp = timepoints[i];
    const volBaseline = allStats[0].totalVolume;
    const volCurrent = allStats[i].totalVolume;

    const targetResponse =
      i === 0
        ? { result: '', message: 'Baseline', pct: 0 }
        : getRecipVolumeResponse(volBaseline, volCurrent, criteria);

    const hasNew = detectNewLesionPresence(
      newAtTp,
      newNamesAtTp,
      i,
      criteria.newLesionAccounting
    );
    const newLesionResponse =
      i === 0
        ? { result: '', message: 'Baseline' }
        : getRecipNewLesionResponse(hasNew);

    const overallResponse = getRecipOverallResponse(
      allStats,
      newAtTp,
      newNamesAtTp,
      priorOverallResults,
      criteria,
      i
    );

    const userAssessment =
      assessments.find(a => a.SeriesInstanceUID === tp.SeriesInstanceUID) || {};
    if (userAssessment.targetResponse) {
      targetResponse.result = userAssessment.targetResponse;
      targetResponse.isOverridden = true;
    }
    if (userAssessment.newLesionResponse) {
      newLesionResponse.result = userAssessment.newLesionResponse;
      newLesionResponse.isOverridden = true;
    }
    if (userAssessment.overallResponse) {
      overallResponse.result = userAssessment.overallResponse;
      overallResponse.isOverridden = true;
    }

    priorOverallResults.push(overallResponse.result);

    ai_assessments.push({
      id: tp.id,
      SeriesDate: tp.SeriesDate,
      SeriesInstanceUID: tp.SeriesInstanceUID,
      SliceThickness: tp.SliceThickness,
      targetStat: { totalVolume: volCurrent, count: allStats[i].count },
      psmaVolBaseline: volBaseline,
      psmaVolCurrent: volCurrent,
      hasBloodPool: allStats[i].hasBloodPool,
      targetResponse,
      nonTargetResponse: { result: '', message: '' },
      newLesionResponse,
      steroidsUse: { result: 'N/A', message: '' },
      clinicalStatus: { result: 'N/A', message: '' },
      overallResponse,
    });
  }

  log('getRecipAssessments output', ai_assessments);
  return ai_assessments;
}
